import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Clock, AlertCircle, TrendingDown } from 'lucide-react'
import { iceSmoothScroll } from '../utils/smoothScroll'

const Problem = () => {
  const { t } = useTranslation()
  const handleScrollToServices = (e) => {
    e.preventDefault()
    iceSmoothScroll('#services', 1200)
  }
  const problems = [
    {
      icon: <Clock className="w-8 h-8 text-red-400" />,
      title: t('problem.card1Title', "Hours Wasted on Manual Work"),
      description: t('problem.card1Desc', "Copying data between spreadsheets and CRM systems, sending the same emails over and over, and chasing leads instead of closing deals.")
    },
    {
      icon: <AlertCircle className="w-8 h-8 text-red-400" />,
      title: t('problem.card2Title', "Leads Slipping Away"),
      description: t('problem.card2Desc', "Poor follow-up system means losing hot leads. You're losing revenue every day due to forgotten prospects.")
    },
    {
      icon: <TrendingDown className="w-8 h-8 text-red-400" />,
      title: t('problem.card3Title', "Competitors Are Moving Faster"),
      description: t('problem.card3Desc', "While you're stuck in spreadsheets, competitors are automating and growing. The gap widens every day.")
    }
  ]

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.12,
        duration: 0.45,
        ease: [0.25, 0.1, 0.25, 1],
      },
    }),
  }

  return (
    <section className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0a0a0f] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#1a1a2e]/30 to-[#0a0a0f]"></div>
      
      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-white">
            {t('problem.heading1', "Tired of Manual Work")}{' '}
            <span className="text-red-400">{t('problem.heading2', "Killing Your Growth?")}</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400">
            {t('problem.description', "You're wasting hours every week on tasks that should be automated. While you're here, competitors are automating and growing faster.")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-8 mb-10 sm:mb-16">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
              className="text-center p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-red-500/30 transition-all duration-300"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-2xl mb-6">
                {problem.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{problem.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Solution Teaser */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-gradient-to-r from-purple-500/20 via-[#1a1a2e] to-purple-500/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 border border-white/10"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                {t('problem.solution', 'We Fix That.')}
              </h3>
              <p className="text-base sm:text-xl text-gray-300 max-w-xl">
                {t('problem.solutionDesc', 'We build n8n workflows that eliminate repetitive tasks and give you back')}{' '}
                <span className="text-purple-400 font-bold">{t('problem.solutionHighlight', '20+ hours every week')}</span>.
              </p>
            </div>
            <a 
              href="#services" 
              onClick={handleScrollToServices}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-purple-600 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 active:scale-[0.98] hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)] whitespace-nowrap"
            >
              {t('problem.cta', 'See How We Do It')}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Problem